import type { SurveyAttribute, SurveyAttributeOptionScores } from '@engagement-web-app/common';
import {
    BarController,
    BarElement,
    CategoryScale,
    type ChartData,
    Chart as ChartJS,
    type ChartOptions,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from 'chart.js';
import { useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Section, SectionHeader } from '@/components/Section';
import { Select } from '@/components/Select';
import { Skeleton } from '@/components/Skeleton';
import { formatNumber, sortByScore } from '@/utils';
import { customCanvasBackgroundColorOptions, customCanvasBackgroundColorPlugin } from '@/utils/chart';
import { AttributeFilter } from '../categories/AttributeFilter';
import * as sharedStyles from '../styles.css';

ChartJS.register(CategoryScale, LinearScale, BarController, BarElement, Title, Tooltip, Legend);

type DiAverageChartPerAttributesProps = {
    /**
     * `null` until the user picks an attribute setting, which is what triggers the
     * fetch — so unlike `attributes` this staying null is not a loading state.
     */
    scores: SurveyAttributeOptionScores[] | null;
    /** `null` until the user picks an attribute setting. See `scores`. */
    previousScores: SurveyAttributeOptionScores[] | null;
    /** `null` while still loading. Required to operate: they populate the filter. */
    attributes: SurveyAttribute[] | null;
    onSettingChange: (id: string | null) => void;
    /** Opt-in: omit when this data has no independent failure mode to report. */
    isError?: boolean;
    onRetry?: () => void;
};

type SortOrder = 'asc' | 'desc';

const LIST_SIZE = 10;

export function DiAverageChartPerAttributes({
    attributes,
    isError,
    onRetry,
    ...props
}: DiAverageChartPerAttributesProps) {
    if (isError)
        return <ErrorOverlay active onRetry={onRetry} message="DI 平均グラフ - 属性比較の取得に失敗しました。" />;

    if (!attributes) return <DiAverageChartPerAttributesSkeleton />;

    return <DiAverageChartPerAttributesContent attributes={attributes} {...props} />;
}

// Only `attributes` narrows — the scores are user-triggered, so they stay nullable.
type DiAverageChartPerAttributesContentProps = Omit<
    DiAverageChartPerAttributesProps,
    'attributes' | 'isError' | 'onRetry'
> & {
    attributes: SurveyAttribute[];
};

// Split from the buffer above so its hooks are never called conditionally.
function DiAverageChartPerAttributesContent({
    scores,
    previousScores,
    attributes,
    onSettingChange,
}: DiAverageChartPerAttributesContentProps) {
    const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([]);
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

    function handleSelectionChange(options: SurveyAttribute['options'], settingId: string | null) {
        setSelectedOptionIds(options.map((o) => o.id));
        onSettingChange(settingId);
    }

    const sortedScores = useMemo(() => {
        if (!scores) return [];
        return scores
            .filter((s) => selectedOptionIds.includes(s.id))
            .sort(sortByScore('di', sortOrder))
            .slice(0, LIST_SIZE);
    }, [scores, selectedOptionIds, sortOrder]);

    const data: ChartData<'bar'> = useMemo(() => {
        return {
            labels: sortedScores.map((d) => d.name),
            datasets: previousScores
                ? [
                      {
                          label: '今年',
                          data: sortedScores.map((d) => d.di),
                          backgroundColor: '#3b82f6',
                      },
                      {
                          label: '前年',
                          data: sortedScores.map((d) =>
                              d.previousId ? (previousScores.find((s) => s.id === d.previousId)?.di ?? null) : null
                          ),
                          backgroundColor: '#9ca3af',
                      },
                  ]
                : [
                      {
                          label: '今年',
                          data: sortedScores.map((d) => d.di),
                          backgroundColor: '#3b82f6',
                      },
                  ],
        };
    }, [sortedScores, previousScores]);

    const options: ChartOptions<'bar'> = useMemo(() => {
        return {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            scales: {
                x: {
                    min: 0,
                    max: 100,
                    grid: { color: '#e5e7eb' },
                },
                y: {
                    grid: { color: '#e5e7eb' },
                },
            },
            plugins: {
                legend: { position: 'bottom' },
                tooltip: {
                    mode: 'index',
                    callbacks: {
                        title: (items) => items[0].label,
                        label: (item) =>
                            `${item.dataset.label}：${item.parsed.x ? formatNumber(item.parsed.x) : 'N/A'}`,
                    },
                },
                ...customCanvasBackgroundColorOptions,
            },
        };
    }, []);

    return (
        <Section flex>
            <SectionHeader
                title="DI 平均グラフ - 属性比較"
                description="部署や年代などの属性ごとに DI 平均を並べ、前回サーベイの値と比較します。属性の種類と並び順は切り替えられます。対象の回答者が 3 人未満の属性は、スコアが表示されません。"
            />

            <Flex direction="column" gap="6" align="start">
                <Flex gap="2" align="center">
                    {attributes.length > 0 && (
                        <AttributeFilter
                            attributes={attributes}
                            selectedOptionIds={selectedOptionIds}
                            restrictToSingleSetting
                            onSelectionChange={handleSelectionChange}
                        />
                    )}

                    <Select value={sortOrder} onChange={(e) => setSortOrder(e.target.value as SortOrder)}>
                        <option value="desc">高い順</option>
                        <option value="asc">低い順</option>
                    </Select>
                </Flex>

                <div className={sharedStyles.chartContainer}>
                    <Bar data={data} options={options} plugins={[customCanvasBackgroundColorPlugin]} />
                </div>
            </Flex>
        </Section>
    );
}

export function DiAverageChartPerAttributesSkeleton() {
    return (
        <Section flex>
            <Flex direction="column" gap="6" align="start">
                <Flex direction="column" gap="2" fullWidth>
                    <Skeleton.Line width="40%" height="xl" />
                    <Skeleton.Line width="70%" height="sm" />
                </Flex>

                <Flex gap="2" align="center">
                    <Skeleton.Box width={120} height={42} />
                    <Skeleton.Box width={120} height={42} />
                </Flex>

                <Skeleton.Box width="100%" height={300} />
            </Flex>
        </Section>
    );
}
